import { blobToErrorResponse, normalizeError, showErrorResponseNotification } from "@/utils/apiUtils";
import { Notification } from "@/utils/notificationUtils";
import { AxiosError } from "axios";
import { t } from "i18next";
import { endSession } from "../../modules/auth/session";
import { getAuthSnapshot } from "../../modules/auth/store";
import { isAuthTokenValid } from "../../modules/auth/utils";
import { ErrorResponse, NormalizedError } from "../types";
import { apiService } from "./apiService";
import { HttpStatus } from "./http";

export const createApiServiceInterceptors = () => {
  apiService.interceptors.request.use(async (config) => {
    const { session, selectedAccount } = getAuthSnapshot();

    if (isAuthTokenValid(session)) {
      config.headers.Authorization = `Bearer ${session.token}`;
      config.headers["Account-Id"] = selectedAccount.accountId;
    }

    return config;
  });

  apiService.interceptors.response.use(
    (response) => response,
    async (error: AxiosError): Promise<NormalizedError> => {
      if (error.response) {
        let errorResponse: ErrorResponse;
        // The request was made and the server responded with a status code that falls out of the range of 2xx
        const errorResponseData = error.response.data as ErrorResponse | Blob;
        if (errorResponseData instanceof Blob) {
          errorResponse = await blobToErrorResponse(errorResponseData);
        } else {
          errorResponse = errorResponseData;
        }

        showErrorResponseNotification(errorResponse);

        if (errorResponse.exceptionMessage) {
          console.error("DEBUG: server response exception message -> " + errorResponse.exceptionMessage);
        }

        const { session } = getAuthSnapshot();
        const sentAuthorization = error.config?.headers.Authorization;

        // A wrong password answers 401 too, with no session to end. A request sent with another token - a refresh
        // overtaken by a new login - must leave the new session alone.
        if (
          errorResponse.status === HttpStatus.UNAUTHORIZED &&
          session &&
          (!sentAuthorization || sentAuthorization === `Bearer ${session.token}`)
        ) {
          endSession();
        }

        return Promise.reject(normalizeError(errorResponse.status, errorResponse.message, errorResponse.violations));
      }

      if (error.request) {
        // The request was made but no response was received
        Notification.getNotification().error({
          title: t("error.serverUnreachable.title"),
          description: t("error.serverUnreachable.text"),
        });
      } else {
        // Some other error occurred
        Notification.getNotification().error({
          title: t("error.unexpected.title"),
          description: t("error.unexpected.message"),
        });
      }

      return Promise.reject(normalizeError(undefined));
    },
  );
};
